import {
  Container,
  Form,
  getTextInputProps,
  InputField,
  InputItem,
  InputUrlField,
  LinkButton,
  Page,
  PageTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import { Button } from '@windmill/react-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { readCategoryOptions, uploadIcon } from '../../common/api/application';
import { DragDropUpload } from '../../common/components/DragDropUpload';
import {
  EXTERNAL_APP_HOSTING_LINKS,
  INTERNAL_APP_HOSTING_LINKS,
} from '../../common/constants';
import { AppType, HostingLocation } from '../../common/enums';
import { DeveloperOrganizationContextProps } from '../../common/providers/types';
import {
  getApplicationSubmission,
  setApplicationSubmission,
} from '../../common/reducers/applicationSubmissionSlice';
import {
  getAppUrlsUpdatedWithFormHostingLinks,
  getFormHostingLinksFromAppUrls,
  isAppLinkCompatibleWithHostingChoice,
} from '../../common/utilities/applicationUtitility';
import { AppUrlChoices } from './types';

interface AppPublishForm extends AppUrlChoices {
  listingName: string;
  urlPath: string;
  appType: string;
  shortDescription: string;
  longDescription: string;
  version: string;
  categories: string[];
  hostingLocation: string;
  consoleCompatible: boolean;
}

const appHostingLinks = EXTERNAL_APP_HOSTING_LINKS.concat(
  INTERNAL_APP_HOSTING_LINKS
);

export const ApplicationPublishInput: React.FunctionComponent = () => {
  const { applicationId } = useParams();
  const { currentOrg, currentTenant } =
    useOrganization() as DeveloperOrganizationContextProps;
  const { currentTeam } = useTeam();

  const [application, setApplication] = React.useState<ApplicationSubmission>();
  const [categoryOptions, setCategoryOptions] = React.useState<
    ApplicationCategory[]
  >([]);

  const [iconFilename, setIconFilename] = useState<string>();
  const [isIconUploading, setIsIconUploading] = React.useState<boolean>(false);
  const [iconError, setIconError] = React.useState<string>('');
  const [hostingLocation, setHostingLocation] = React.useState<HostingLocation>(
    HostingLocation.PLATFORM
  );
  const [appUrlChoices, setAppUrlChoices] = React.useState<AppUrlChoices>({});

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const appSubmissionState = useSelector(getApplicationSubmission);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AppPublishForm>({
    mode: 'onChange',
    defaultValues: useMemo(() => {
      return { ...appSubmissionState, ...appUrlChoices };
    }, [appSubmissionState, appUrlChoices]),
  });

  // Page Load
  React.useEffect(() => {
    if (!appSubmissionState?.appUrls) {
      return;
    }

    const hostingLoc =
      appSubmissionState.hostingLocation == 'other'
        ? HostingLocation.EXTERNAL
        : HostingLocation.PLATFORM;
    setHostingLocation(hostingLoc);
    setIconFilename(appSubmissionState.iconFileName);

    if (Object.keys(appUrlChoices).length === 0) {
      const urls = appSubmissionState.appUrls;
      const choices = getFormHostingLinksFromAppUrls(urls, appHostingLinks);
      setAppUrlChoices(choices);
      reset({ ...appSubmissionState, ...choices });
    }
  }, [appSubmissionState]);

  React.useEffect(() => {
    if (
      applicationId &&
      currentOrg &&
      currentTenant &&
      currentTeam &&
      !application
    ) {
      setApplication(appSubmissionState);
    }
  }, [applicationId, currentOrg, currentTeam, currentTenant]);

  React.useEffect(() => {
    const getCategoryOptions = async () => {
      const catOptions = await readCategoryOptions();
      setCategoryOptions(catOptions);
    };
    getCategoryOptions();
  }, []);

  const resetHostingLocation = useCallback((loc: HostingLocation) => {
    setAppUrlChoices({});
    setHostingLocation(loc);
  }, []);

  const uploadAppIcon = async (file: File) => {
    if (
      !application ||
      !currentOrg ||
      !currentTeam ||
      !currentTenant ||
      currentTenant.orgId != currentOrg.id
    ) {
      return;
    }

    if (file) {
      setIsIconUploading(true);
      await uploadIcon(
        currentOrg.id,
        currentTeam.id,
        application.appId,
        application.versionId,
        file
      );

      setIconFilename(file.name);
      setIsIconUploading(false);
    }
  };

  const removeAppIcon = async () => {
    setIconFilename(undefined);
  };

  const onSubmit = handleSubmit(async (form) => {
    if (
      !application ||
      !currentOrg ||
      !currentTeam ||
      !currentTenant ||
      currentTenant.orgId != currentOrg.id
    ) {
      return;
    }

    if (application) {
      const app = setSubmissionFromForm(application, form);

      dispatch(setApplicationSubmission(app));

      navigate(`/application/${applicationId}/publish/summary`, {
        replace: false,
      });
    }
  });

  const setSubmissionFromForm = (
    app: ApplicationSubmission,
    form: AppPublishForm
  ): ApplicationSubmission => {
    const submission: ApplicationSubmission = { ...app };
    submission.listingName = form.listingName;
    submission.appType = form.appType;
    submission.shortDescription = form.shortDescription;
    submission.version = form.version;
    submission.categories = form.categories;
    submission.hostingLocation = form.hostingLocation;
    submission.iconFileName = iconFilename;
    submission.consoleCompatible = form.consoleCompatible;

    const formAppUrls: { [key: string]: string | undefined } =
      appHostingLinks.reduce((accumulator, link) => {
        accumulator[link] = form[link];
        return accumulator;
      }, {});

    submission.appUrls = getAppUrlsUpdatedWithFormHostingLinks(
      submission.appUrls,
      appHostingLinks,
      formAppUrls
    ).filter((link) =>
      isAppLinkCompatibleWithHostingChoice(link, hostingLocation)
    );

    return submission;
  };

  if (
    !application ||
    !currentOrg ||
    !currentTenant ||
    currentTenant.orgId != currentOrg.id
  ) {
    return null;
  }

  return (
    <Page className="flex-col items-center">
      <Container
        className="mb-os1-spacing flex flex-col justify-start gap-6"
        size="medium"
      >
        <Form
          onSubmit={onSubmit}
          title={
            <div className="flex flex-row items-center gap-x-3">
              <PageTitle>{application.listingName}</PageTitle>
            </div>
          }
        >
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={false}
              errors={errors}
              inputKey="listingName"
              placeholder={application.listingName}
              register={register}
              testId="listingName-input"
              title="App Name"
            />
          </div>
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={true}
              errors={errors}
              inputKey="urlPath"
              placeholder={application.urlPath}
              register={register}
              testId="urlPath-input"
              title="App Path URL"
            />
          </div>
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={true}
              errors={errors}
              inputKey="appType"
              placeholder={application.appType}
              register={register}
              testId="appType-input"
              title="App Type"
            />
          </div>
          <div className="flex flex-row items-center gap-x-3">
            <InputItem title="Category">
              <fieldset>
                <span>(Select all that apply)</span>
                {categoryOptions &&
                  categoryOptions.map((option) => (
                    <div className="form-check" key={option.id}>
                      <input
                        className="form-check-input appearance-none h-4 w-4 border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                        data-testid={option.id}
                        id={option.id}
                        type="checkbox"
                        value={option.id}
                        {...register('categories', { required: true })}
                      />
                      <label
                        className="form-check-label inline-block text-gray-800"
                        htmlFor={option.id}
                      >
                        {option.displayName}
                      </label>
                    </div>
                  ))}
                {errors.categories && (
                  <div className="text-xs mt-2 text-red-600 dark:text-red-400 left-0">
                    Select a category.
                    {errors.categories.message}
                  </div>
                )}
              </fieldset>
            </InputItem>
          </div>
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={false}
              errors={errors}
              inputKey="shortDescription"
              placeholder={application.shortDescription}
              register={register}
              testId="shortDescription-input"
              title="Description"
            />
          </div>
          <div className="flex flex-col items-left gap-x-3">
            <InputField
              className="w-full"
              disabled={true}
              errors={errors}
              inputKey="version"
              placeholder={application.version}
              register={register}
              testId="version-input"
              title="Version"
            />
          </div>
          <div className="flex flex-row items-center gap-x-3">
            <DragDropUpload
              acceptedFileTypes={[
                'image/jpg',
                'image/jpeg',
                'image/png',
                'image/gif',
              ]}
              error={iconError}
              initialFilename={iconFilename}
              isUploading={isIconUploading}
              multipleFiles={false}
              onFileSelected={uploadAppIcon}
              onFileUnselected={removeAppIcon}
              onInvalidFile={(msg) => {
                setIconError(msg);
              }}
              title="Icon"
            />
          </div>
          <div className="flex flex-row items-center gap-x-3">
            <InputItem title="Where are you hosting your application?">
              <div className="flex items-center mb-2">
                <input
                  {...register('hostingLocation')}
                  checked={hostingLocation === HostingLocation.PLATFORM}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  id="os1-platform"
                  name="hostingLocation"
                  onClick={() => resetHostingLocation(HostingLocation.PLATFORM)}
                  type="radio"
                  value="os1-platform"
                />
                <label
                  className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  htmlFor="os1-platform"
                >
                  OS1 Platform
                </label>
              </div>
              <div className="flex items-center">
                <input
                  {...register('hostingLocation')}
                  checked={hostingLocation === HostingLocation.EXTERNAL}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  id="other"
                  name="hostingLocation"
                  onClick={() => resetHostingLocation(HostingLocation.EXTERNAL)}
                  type="radio"
                  value="other"
                />
                <label
                  className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  htmlFor="other"
                >
                  Other
                </label>
              </div>
            </InputItem>
          </div>
          <div className="flex flex-col items-left gap-x-3">
            {hostingLocation === HostingLocation.PLATFORM && (
              <div>
                <InputUrlField
                  disabled={false}
                  errors={errors}
                  inputKey="userGuideUrl"
                  placeholder={appUrlChoices.userGuideUrl}
                  register={register}
                  required={true}
                  testId="userGuideUrl-input"
                  title="User Guide URL"
                />
              </div>
            )}
            {hostingLocation === HostingLocation.EXTERNAL && (
              <div>
                <InputUrlField
                  disabled={false}
                  errors={errors}
                  inputKey="serverUrl"
                  placeholder={appUrlChoices.serverUrl}
                  register={register}
                  required={true}
                  testId="serverUrl-input"
                  title="Server URL"
                />
                <InputUrlField
                  disabled={false}
                  errors={errors}
                  inputKey="uiHomeUrl"
                  placeholder={appUrlChoices.uiHomeUrl}
                  register={register}
                  required={true}
                  testId="uiHomeUrl-input"
                  title="UI Home URL"
                />
                <InputUrlField
                  disabled={false}
                  errors={errors}
                  inputKey="userGuideUrl"
                  placeholder={appUrlChoices.userGuideUrl}
                  register={register}
                  required={true}
                  testId="userGuideUrl-input"
                  title="User Guide URL"
                />
              </div>
            )}
            {application.appType == AppType.web && (
              <InputItem title="Console Compatible" type="checkbox">
                <input
                  id="field-console-compatible"
                  type="checkbox"
                  {...getTextInputProps({
                    inputKey: 'consoleCompatible',
                    errors,
                    register,
                    className: 'border-gray-400 rounded-sm mr-3 mt-1',
                    type: 'checkbox',
                    required: false,
                  })}
                />
                <div className="flex flex-col">
                  <span className="text-xs">
                    App is compatible with the console app launch experience.
                  </span>
                  <div>
                    <LinkButton
                      className="text-sm"
                      linkText="More info"
                      onClick={() => window.open('https://docs.getos1.com')}
                    />
                  </div>
                </div>
              </InputItem>
            )}
          </div>
          <div className="flex flex-row items-center gap-x-3">
            <Button className="h-10" onClick={onSubmit} size="large">
              Next - Preview
            </Button>
          </div>
        </Form>
      </Container>
    </Page>
  );
};
